import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import GiveSupport from '../payment/give-support/GiveSupport';
import GenericHelmet from '../generic-helmet/GenericHelmet';
import { trackPageView } from '../../support-and-tracking/analytics-actions';
import { triggerCanceledPaymentRaw, useIntercomFunction } from '../../support-and-tracking/intercom-actions';
import { getProjectFromId } from '../projects-reducer';
import { getFormActions } from '../../utils/form-helpers';
import { getSharingTextForProject, GoalTypes, isProjectADugnad, isSuccessful } from '../project-model';
import vippsFormConfig from '../../payment/vipps-form-config';
import formConfig from '../../payment/payment-form-config';
import { fetchProjectCard, fetchProjectTransactionAggregates, fetchRewards } from '../project-actions';
import Main from '../../frame/main/Main';
import { VIPPS_PAYMENT_IS_DISABLED } from '../../constants/features/constants';
import { hasWindow } from '../../utils';
import NotFound from '../not-found/NotFound';
import ErrorMessage from '../../components/info-messages/ErrorMessage';
import ProjectCardPayment from '../payment/payment-card/ProjectCardPayment';
import ButtonText from '../../components/button/button-text/ButtonText';
import ProjectContext from '../project-context';
import { useUser } from '../../user/useUser';
import { fetchSubproject } from '../subproject/subproject-actions';
import { getSubproject } from '../subproject-selectors';
import { Cart } from '../payment/cart/Cart';
import { getCartItems, getCartSum } from '../../cart/cart-reducer';
import { fetchCart } from '../../cart/cart-actions';
import GeneralError from '../../components/GeneralError';
import { useFundAsGift } from '../../components/hooks/useFundAsGift';
import {
    getFundAsGiftPath,
    getFundAsGiftPaymentPath,
    projectIsFundAsGiftProject,
} from '../fund-as-gift/fund-as-gift-model';
import { RECOMMENDED_EVENT_VIEW_CART } from '../../support-and-tracking/analytics-events';
import { trackProjectPayment } from '../../support-and-tracking/tracking-functions';
import { ValidationWrapper } from './ValidationWrapper';
import { GiverResponsibility } from '../payment/giver-responsibility/GiverResponsibility';
import { isAnyRewardNeitherLotteryNorProductReward } from '../payment/cart/cart-model';
import styles from './project-payment.module.less';
import { cachedRedirect } from '../../utils/cache-utils';
import I18nText from '../i18n/I18nText';
import { CANCEL_TEXT_KEY, isUserUsingNorwegianLanguageOnProject } from '../i18n/project-i18n-model';

const { clearAll } = getFormActions(vippsFormConfig);
const { setFormFields } = getFormActions(formConfig);

const ProjectPayment = () => {
    const params = useParams();
    const location = useLocation();
    const query = queryString.parse(location.search);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const triggerCanceledPayment = useIntercomFunction(triggerCanceledPaymentRaw);

    const project = useSelector((state) => getProjectFromId(state, params.projectId));
    const isDugnad = isProjectADugnad(project);
    const subprojectId = query.subproject ? parseInt(query.subproject, 10) : undefined;
    const subproject = useSelector((state) => getSubproject(state, subprojectId));
    const { fundAsGiftCode } = useFundAsGift();
    const user = useUser();
    const baseURL = useSelector((state) => state.baseURL);
    const isFetchingProject = useSelector((state) => state.projects.isFetchingCards);
    const paymentEnabled = useSelector((state) => state.features.paymentEnabled);
    const paymentErrorMessage = useSelector((state) => state.features.paymentErrorMessage);
    const vippsPaymentDisabled = useSelector((state) => state.features[VIPPS_PAYMENT_IS_DISABLED]);
    const paymentForm = useSelector((state) => state.paymentForm);
    const cartSum = useSelector((state) => getCartSum(state, project?.id));
    const cartErrors = useSelector((state) => state.cart.errors);
    const displayCart = useMemo(() => !!cartSum, [cartSum]);
    const cartItems = useSelector((state) => getCartItems(state, project?.id)) || [];

    useEffect(() => {
        dispatch(fetchCart({ projectId: params.projectId }));
    }, [dispatch, params.projectId]);

    useEffect(() => {
        trackPageView({
            urlAsEvent: 'project-payment',
            properties: {
                'project-id': params.projectId,
                subprojectId,
                cart: displayCart,
                'fund-as-gift-code': fundAsGiftCode,
            },
        });

        return () => {
            dispatch(clearAll());
        };
    }, [params.projectId]);

    useEffect(() => {
        if (displayCart && cartSum && cartItems) {
            trackProjectPayment({ event: RECOMMENDED_EVENT_VIEW_CART, cartSum, cartItems });
        }
    }, [cartSum, cartItems?.length]);

    useEffect(() => {
        if (hasWindow()) {
            window.scrollTo(0, 0);
        }
    }, []);

    const pathToProject = () => {
        if (fundAsGiftCode) {
            return getFundAsGiftPath(fundAsGiftCode);
        }
        return `/project/${project?.id}${subprojectId ? `?subproject=${subprojectId}` : ``}`;
    };

    const userCancel = async () => {
        await triggerCanceledPayment(user);
        navigate(pathToProject());
    };

    if (!project || (!project && isFetchingProject)) {
        return (
            <Main>
                <Loading />
            </Main>
        );
    }

    if (subprojectId && subproject?.isError) {
        return (
            <Main>
                <NotFound />
            </Main>
        );
    }

    if (cartErrors) {
        return (
            <Main>
                <GeneralError>{cartErrors.global}</GeneralError>
            </Main>
        );
    }

    const dismissSupport = () => {
        dispatch(
            setFormFields({
                processingPayment: false,
            })
        );
        userCancel();
    };

    const { processingPayment } = paymentForm;

    return (
        <Main>
            <ProjectContext.Provider value={project}>
                <GenericHelmet
                    title={project.title}
                    description={getSharingTextForProject(project)}
                    path={location.pathname}
                    imageUrl={project.img_url}
                    projectId={project.id}
                >
                    <div className={styles.container}>
                        {!!paymentErrorMessage && (
                            <div className={styles.errorMessage}>
                                <p>{paymentErrorMessage}</p>
                            </div>
                        )}
                        {vippsPaymentDisabled && (
                            <div className={styles.vippsDisabledMessage}>
                                <ErrorMessage>Vipps er dessverre utilgjengelig for øyeblikket</ErrorMessage>
                            </div>
                        )}
                        <ValidationWrapper project={project} paymentEnabled={paymentEnabled}>
                            {displayCart ? <Cart /> : <GiveSupport />}
                        </ValidationWrapper>
                        <div>
                            <GiverResponsibility
                                showRewardText={isAnyRewardNeitherLotteryNorProductReward({ rewards: cartItems })}
                            />
                            {!processingPayment && displayCart && (
                                <ButtonText
                                    type={'button'}
                                    onClick={dismissSupport}
                                    className={styles.cancelButtonCart}
                                >
                                    Tilbake til {isDugnad ? 'dugnaden' : 'spleisen'}
                                </ButtonText>
                            )}
                            {!!project.profile?.giverIsNotAnonymousForOwner && (
                                <div className={styles.paymentInformation}>
                                    På denne spleisen er du som giver ikke anonym overfor innsamler. Du kan imidlertid
                                    selv velge om du ønsker å stå oppført offentlig på giverlisten på spleisen.
                                </div>
                            )}
                            {!isSuccessful(project) && project.goaltype === GoalTypes.TARGET && (
                                <div className={styles.paymentInformation}>
                                    Du gir nå et bidrag til en spleis med målbeløp. Hvis innsamlingen ikke når målet
                                    sitt, fører vi pengene tilbake til kontoen din.
                                </div>
                            )}
                            {isUserUsingNorwegianLanguageOnProject(project) && (
                                <div className={styles.projectCard}>
                                    <ProjectCardPayment project={project} subproject={subproject} baseURL={baseURL} />
                                </div>
                            )}
                            {!processingPayment && (
                                <ButtonText onClick={dismissSupport} className={styles.cancelButtonStandard}>
                                    <I18nText textKey={CANCEL_TEXT_KEY} />
                                </ButtonText>
                            )}
                        </div>
                    </div>
                </GenericHelmet>
            </ProjectContext.Provider>
        </Main>
    );
};

export const loader = async ({ dispatch, params, request, getState }) => {
    const { projectId } = params;
    const url = new URL(request.url);
    const subprojectId = url.searchParams.get('subproject');

    const promises = [
        dispatch(fetchProjectCard(projectId)),
        dispatch(fetchProjectTransactionAggregates(projectId)),
        dispatch(fetchRewards(projectId)),
    ];
    if (subprojectId) {
        promises.push(dispatch(fetchSubproject({ projectId: params.projectId, subprojectId })));
    }
    await Promise.all(promises);
    const project = getProjectFromId(getState(), projectId);
    if (projectIsFundAsGiftProject(project)) {
        return cachedRedirect(getFundAsGiftPaymentPath(project?.profile?.fundAsGiftCode));
    }
};

export default ProjectPayment;
